import { useMemo } from 'react';
import { Coins } from 'lucide-react';

import { useCredits, ActionType } from '../../hooks/useCredits';
import { Badge, Spinner } from '../ui';

import styles from './CreditsBadge.module.css';

const ACTION_LABEL: Record<ActionType, string> = {
  simulation_official: 'Simulados',
  correction: 'Correções',
  exam_creation: 'Simulados criados',
  student_correction: 'Correções de aluno',
};

interface Props {
  /** layout: 'compact' (badge no header) | 'full' (faixa horizontal) */
  variant?: 'compact' | 'full';
  /** Ao clicar no badge, navega para a tela de Conta (#creditos). Default true */
  linkToConta?: boolean;
  /**
   * Action types a ocultar deste badge. Ex.: o badge do menu principal oculta
   * `correction` (saldo de correção com transcrição) — esse saldo é exibido
   * apenas na área de Correções.
   */
  exclude?: ActionType[];
}

/**
 * Fase 6 — Badge reutilizável de saldo de créditos.
 *
 * Filtra os action types pelos que são relevantes ao `accountType` do
 * usuário (definido pelo backend em `GET /me/credits`):
 *  - `student` → `simulation_official`
 *  - `business` → `correction`, `exam_creation`, `student_correction`
 *
 * Usado em:
 *  - Dashboard (variant='compact', via PageHeader.actions)
 *  - Conta usa o CreditsPanel detalhado em vez deste badge.
 */
export default function CreditsBadge({
  variant = 'compact',
  linkToConta = true,
  exclude,
}: Props) {
  const { balance, loading, error } = useCredits();

  const items = useMemo(() => {
    if (!balance) return [];
    return balance.actionTypes
      .filter((at) => !exclude?.includes(at))
      .map((at) => ({
        actionType: at,
        label: ACTION_LABEL[at],
        total: balance.totals[at] ?? 0,
      }));
  }, [balance, exclude]);

  if (loading && !balance) {
    return (
      <div className={styles.wrapper} aria-label="Carregando créditos">
        <Spinner size="sm" />
      </div>
    );
  }

  if (error || !balance) return null;
  if (items.length === 0) return null;

  const content = (
    <div className={`${styles.wrapper} ${styles[variant]}`}>
      <Coins size={16} aria-hidden />
      {items.map((item) => (
        <span key={item.actionType} className={styles.item}>
          <span className={styles.label}>{item.label}</span>
          <Badge variant={item.total > 0 ? 'success' : 'warning'} size="sm">
            {item.total}
          </Badge>
        </span>
      ))}
    </div>
  );

  if (linkToConta) {
    return (
      <a href="/conta#creditos" className={styles.link} aria-label="Ver detalhes de créditos">
        {content}
      </a>
    );
  }
  return content;
}
